import { pick } from "lodash";
import {
  type FieldPath,
  type FieldPathValue,
  type FieldPathValues,
  type FieldValues,
  useForm,
  useFormContext,
  useWatch,
} from "react-hook-form";
import type z from "zod";
import { CREATOR_AND_UP } from "common/authorization";
import type { Attachment } from "common/utils/customObjects";
import {
  getSchemaPath,
  getUiSchemaPath,
  type PathArray,
} from "common/utils/formStructure";
import type { GetSubmissionTypeForBuilderQuery } from "../../../../../../generated/graphql";
import type { submissionsBuilderFormSchema } from "./validation/schema";

export type SubmissionsBuilderFormData = z.infer<
  typeof submissionsBuilderFormSchema
>;

export const useBuilderForm = useForm<SubmissionsBuilderFormData>;

export const useBuilderFormContext = useFormContext<SubmissionsBuilderFormData>;

export function useBuilderWatch<
  TFieldValues extends FieldValues = SubmissionsBuilderFormData,
  TFieldNames extends
    readonly FieldPath<TFieldValues>[] = readonly FieldPath<TFieldValues>[],
>(name: readonly [...TFieldNames]): FieldPathValues<TFieldValues, TFieldNames>;
export function useBuilderWatch<
  TFieldValues extends FieldValues = SubmissionsBuilderFormData,
  TFieldName extends FieldPath<TFieldValues> = FieldPath<TFieldValues>,
>(name: TFieldName): FieldPathValue<TFieldValues, TFieldName>;
export function useBuilderWatch(name: any): any {
  return useWatch({ name });
}

export function getDefaultValues(
  existingSubmissionType: GetSubmissionTypeForBuilderQuery["submissionType"],
  versionId?: string,
) {
  const staticDefaultValues: PartialBy<SubmissionsBuilderFormData, "category"> =
    {
      name: "",
      formStructure: {
        schema: {
          type: "object",
        },
        uiSchema: {},
      },
      modules: [],
      attachments: [],
      canCreateRolesAndGroupIds: CREATOR_AND_UP,
      hiddenFromPublicByDefault: true,
    };

  if (!existingSubmissionType) {
    return staticDefaultValues;
  }

  const existingSubmissionTypeVersion = existingSubmissionType.versions.find(
    version =>
      version.id === (versionId ?? existingSubmissionType.currentVersion.id),
  );

  if (!existingSubmissionTypeVersion) {
    return staticDefaultValues;
  }

  const existingSubmissionTypeFields = pick(existingSubmissionType, [
    "name",
    "description",
    "category",
    "modules",
    "hiddenFromPublicByDefault",
    "successMessage",
    "preamble",
    "postamble",
  ]);

  return {
    ...existingSubmissionTypeFields,
    formStructure: pick(existingSubmissionTypeVersion.formStructure, [
      "schema",
      "uiSchema",
    ]),
    attachments: existingSubmissionType.attachments.map(
      ({ type, customMapIds }) => ({
        type,
        ...(customMapIds ? { customMapIds } : {}),
      }),
    ) as Array<Attachment>,
    notificationEmails:
      existingSubmissionType.notificationEmails?.join("\n") ?? null,
    canCreateRolesAndGroupIds: [
      ...existingSubmissionType.canCreate,
      ...(existingSubmissionType.canCreateGroups.map(group => group.id) ?? []),
    ],
    conditionalRules: existingSubmissionTypeVersion.conditionalRules.map(
      ({ id, condition, effects }) => ({
        id,
        condition,
        effects: effects.map(({ action, targetField }) => ({
          action,
          targetField,
        })),
      }),
    ),
    conditionalRulesUserPrompt:
      existingSubmissionTypeVersion.conditionalRulesUserPrompt,
  };
}

export const DEFAULT_PREAMBLE = "Your record was successfully submitted!";

export const DEFAULT_POSTAMBLE = "Thank you for filling out this form.";

export function getSchemaFormPath(pathArray: PathArray) {
  return ["formStructure.schema", getSchemaPath(pathArray)]
    .filter(Boolean)
    .join(".") as "formStructure.schema" | `formStructure.schema.${string}`;
}

export function getSchemaPropertyFormPath(
  pathArray: PathArray,
  property: string,
) {
  return `${getSchemaFormPath(pathArray)}.${property}` as const;
}

export function getUiSchemaFormPath(pathArray: PathArray) {
  return ["formStructure.uiSchema", getUiSchemaPath(pathArray)]
    .filter(Boolean)
    .join(".") as `formStructure.uiSchema` | `formStructure.uiSchema.${string}`;
}

export function getUiSchemaPropertyFormPath(
  pathArray: PathArray,
  property: string,
) {
  return `${getUiSchemaFormPath(pathArray)}.ui:${property}` as const;
}
